import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { variantSrcSet, dropVariants } from "../lib/imgVariants";
import { readiness } from "../lib/readiness";
import { formatAED, bedroomsLabel } from "../lib/api";

// Image-led, swipeable property slider (31 Oct 2026). Tap a card → listing details page.
export default function PropertyRail({ items = [], testid = "property-rail" }) {
  const ref = useRef(null);
  const by = (dir) => { const el = ref.current; if (el) el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: "smooth" }); };
  if (!items.length) return <div className="h-[420px] bg-sand/60 animate-pulse" aria-hidden="true" />;
  return (
    <div className="relative" data-testid={testid}>
      <div ref={ref} className="flex gap-5 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-4 -mx-6 px-6 md:mx-0 md:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {items.map((p) => {
          const r = readiness(p);
          const bv = p.slug === "bay-views-hayat-island-rak";
          return (
            <Link key={p.slug} to={`/listings/${p.slug}`} className="group snap-start shrink-0 w-[82%] sm:w-[46%] lg:w-[31.5%] block" data-testid={`rail-card-${p.slug}`}>
              <div className="relative aspect-[4/3] overflow-hidden bg-sand">
                <img src={p.hero_image} srcSet={variantSrcSet(p.hero_image)} sizes="(max-width: 640px) 82vw, (max-width: 1024px) 46vw, 31vw"
                  width={1200} height={900} alt={`${p.title} — ${p.community || p.location}`} loading="lazy" decoding="async"
                  onError={(e) => { dropVariants(e.currentTarget); }}
                  className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]" />
                {r && <span className="absolute top-3 left-3 bg-bone/90 text-ink text-[0.6rem] tracking-[0.14em] uppercase px-2.5 py-1">{r}</span>}
              </div>
              <div className="pt-4">
                <div className="text-[0.62rem] tracking-[0.14em] uppercase text-ink/50">{p.community || p.location}</div>
                <h3 className="font-display text-[1.3rem] leading-snug text-ink mt-1 group-hover:text-gold transition-colors line-clamp-1">{p.title}</h3>
                <div className="mt-1 text-ink/70 text-[0.9rem]">
                  {bv ? "Full floor · " : p.bedrooms != null ? `${bedroomsLabel(p.bedrooms)} · ` : ""}
                  <span className="text-ink">{(p.price_aed ?? p.price) ? formatAED(p.price_aed ?? p.price) : "Price on request"}</span>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
      <div className="hidden md:flex absolute -top-16 right-0 gap-2">
        <button type="button" onClick={() => by(-1)} aria-label="Previous properties" className="w-11 h-11 border border-ink/20 hover:border-gold hover:text-gold inline-flex items-center justify-center transition-colors"><ChevronLeft size={18} /></button>
        <button type="button" onClick={() => by(1)} aria-label="Next properties" className="w-11 h-11 border border-ink/20 hover:border-gold hover:text-gold inline-flex items-center justify-center transition-colors"><ChevronRight size={18} /></button>
      </div>
    </div>
  );
}
